/**
 * StalemateConfigFields — the editable body of a {@link TrlStalemateConfig}.
 *
 * Reused in two places so the game-level defaults and a per-node override are
 * authored with identical controls:
 *   • TrlGameSettingsModal (game-level defaults)
 *   • PlanetEditModal (per-node override, seeded from the game defaults)
 *
 * Purely controlled: it renders `value` and reports every edit through
 * `onChange`. Delays are shown in minutes for the admin but stored as seconds.
 */

import type { TrlStalemateConfig, TrlStalemateClue } from '@swtorbit/shared-socket-events';

interface StalemateConfigFieldsProps {
  value: TrlStalemateConfig;
  onChange: (next: TrlStalemateConfig) => void;
  disabled?: boolean;
}

/** Seconds → minutes for display (blank-safe). */
const toMinutes = (seconds: number): string => String(Math.round((seconds / 60) * 100) / 100);
/** Minutes string → clamped non-negative seconds. */
const toSeconds = (minutes: string): number => {
  const m = parseFloat(minutes);
  return Number.isFinite(m) && m >= 0 ? Math.round(m * 60) : 0;
};

export function StalemateConfigFields({ value, onChange, disabled = false }: StalemateConfigFieldsProps) {
  const patchClue = (
    key: 'area' | 'scanner' | 'pinpoint',
    patch: Partial<TrlStalemateClue>,
  ) => {
    onChange({ ...value, [key]: { ...value[key], ...patch } });
  };

  const clueRow = (
    key: 'area' | 'scanner' | 'pinpoint',
    label: string,
    help: string,
  ) => {
    const clue = value[key];
    return (
      <div className="d-flex flex-column gap-2xs">
        <label className="d-flex align-items-center gap-2xs fs-sm">
          <input
            className="checkbox"
            type="checkbox"
            checked={clue.enabled}
            disabled={disabled}
            onChange={(e) => patchClue(key, { enabled: e.target.checked })}
          />
          <strong>{label}</strong>
        </label>
        <span className="fs-xs text-slate">{help}</span>
        <label className="d-flex align-items-center gap-2xs fs-sm">
          After
          <input
            type="number"
            className="input"
            style={{ width: '6rem' }}
            min="0"
            step="0.1"
            value={toMinutes(clue.delaySeconds)}
            disabled={disabled || !clue.enabled}
            onChange={(e) => patchClue(key, { delaySeconds: toSeconds(e.target.value) })}
          />
          minutes of no progress
        </label>
      </div>
    );
  };

  return (
    <div className="d-flex flex-column gap-sm">
      {/* Stalemate clues — the three escalating inactivity hints. */}
      <div className="border-md border-aqua-dark rounded-base p-xs d-flex flex-column gap-xs">
        <span className="fs-xs text-uppercase text-slate">Stalemate clues</span>
        {clueRow('area', 'Area reveal', 'Shows the name of the zone the target is in (e.g. Jundland).')}
        {clueRow('scanner', 'Empowered scanner', 'Grants each member a one-time exact-distance scan charge.')}

        {/* Pinpoint carries an extra obfuscation field */}
        <div className="d-flex flex-column gap-2xs">
          {clueRow('pinpoint', 'Pinpoint estimate', 'Reveals an approximate x/y/z and a map circle that always contains the target.')}
          <label className="d-flex align-items-center gap-2xs fs-sm">
            Circle radius
            <input
              type="number"
              className="input"
              style={{ width: '6rem' }}
              min="0"
              step="1"
              value={String(value.pinpoint.obfuscationMeters)}
              disabled={disabled || !value.pinpoint.enabled}
              onChange={(e) => {
                const raw = parseFloat(e.target.value);
                const obfuscationMeters = Number.isFinite(raw) && raw >= 0 ? Math.round(raw) : 0;
                onChange({ ...value, pinpoint: { ...value.pinpoint, obfuscationMeters } });
              }}
            />
            metres
          </label>
        </div>
      </div>

      {/* Planet name — a standalone display option, not a stalemate clue. */}
      <div className="border-md border-aqua-dark rounded-base p-xs d-flex flex-column gap-xs">
        <span className="fs-xs text-uppercase text-slate">Planet name</span>
        <label className="d-flex align-items-center gap-2xs fs-sm">
          <input
            className="checkbox"
            type="checkbox"
            checked={value.showPlanetLabel}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, showPlanetLabel: e.target.checked })}
          />
          <span>
            <strong>Show planet name to players</strong>
            <span className="fs-xs text-slate d-block">
              When off, the planet&apos;s name is hidden on the map and Round Info overlay.
            </span>
          </span>
        </label>
      </div>
    </div>
  );
}
